import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ErrorCodeEnum } from '@ulysses-inc/harami_api_client'
import { FC, ReactNode } from 'react'
import * as authError from 'src/features/auth/error/state'
import {
  HTTPError,
  isHTTPError,
} from 'src/state/middleware/saga/handleHttpError'
import { store } from 'src/state/store'

/**
 * TanStack Query 経由の API で認証切れエラーが発生した際に、認証エラーモーダルを表示して
 * ログイン画面へ遷移させるためのグローバルハンドラ。
 *
 * Redux Saga 側の interceptions saga (`src/state/ducks/interceptions/sagas.ts`) は
 * 認証切れ以外のエラー（業務エラーの通知表示やデータ不在ウィジェット）もグローバルに扱っているが、
 * 同 saga のコメントにある通り、認証切れ以外をグローバルに扱うのは責務分離の観点から避けるべきとされている。
 * TanStack Query に移行した画面はエラー表示を各画面で自前に持っているため、ここでは認証切れのみを扱う。
 *
 * 個別にエラーハンドリングを行いたい Query / Mutation は、
 * meta: { skipGlobalErrorHandler: true } を設定することでこのハンドラをスキップできる。
 */
// isHTTPError は `code` プロパティの有無のみで判定するため、errors が配列でない可能性に備える。
const toMessage = (errors: unknown, fallback: string): string =>
  Array.isArray(errors) && errors.length > 0 ? errors.join('\n') : fallback

// メンテモードのレスポンス。HTTP ステータスではなくレスポンス本文の status が 503。
// 認証エラー (code 基準) とは別系統で、code を持たないため isHTTPError では判定できない。
type MaintenanceErrorBody = { status: number; errors: Array<string> }

const isMaintenanceErrorBody = (body: unknown): body is MaintenanceErrorBody =>
  typeof body === 'object' &&
  body !== null &&
  (body as { status?: unknown }).status === 503

const dispatchAuthDialogByCode = (error: HTTPError) => {
  switch (error.code) {
    case ErrorCodeEnum.AuthenticationExpired: {
      store.dispatch(
        authError.showDialog({
          summary: '認証期限切れ',
          message: '認証期限切れのため、ログイン画面へ遷移します',
        }),
      )
      return
    }
    case ErrorCodeEnum.ServiceAssignmentRequiredError: {
      store.dispatch(
        authError.showDialog({
          summary: '権限不足',
          message: toMessage(error.errors, '権限が不足しています'),
        }),
      )
      return
    }
    case ErrorCodeEnum.InvalidAudienceError: {
      store.dispatch(
        authError.showDialog({
          summary: '認証エラー',
          message: '再ログインが必要です',
        }),
      )
      return
    }
    case ErrorCodeEnum.CredentialsNotFoundError:
    case ErrorCodeEnum.AccessTokenInactiveError: {
      store.dispatch(
        authError.showDialog({
          summary: '認証期限切れ',
          message: toMessage(
            error.errors,
            '認証期限切れのため、再度ログインしてください',
          ),
        }),
      )
      return
    }
    default:
      return
  }
}

export const handleGlobalAuthError = (
  error: unknown,
  meta?: Record<string, unknown>,
) => {
  if (meta?.['skipGlobalErrorHandler'] === true) return

  // `.catch(handleHTTPError)` で unwrap 済みのエラー（top-level に code を持つ）。
  if (isHTTPError(error)) {
    dispatchAuthDialogByCode(error)
    return
  }

  // OpenAPI 生成クライアントは非 2xx 時に生の Response をそのまま throw するため、
  // unwrap していないクエリではここに Response が届く。本文を読んで同じ判定にかける。
  if (error instanceof Response) {
    error
      .clone()
      .json()
      .then(body => {
        // メンテモード (本文 status === 503) は認証判定の前段で /maintenance へ遷移させる。
        // unwrap 経路では handleHTTPError が同等の遷移を行うが、生 Response 経路はそれを通らないため
        // ここで揃える。汎用 503 (ALB 等) は本文が JSON でなく上の json() で例外になり到達しない。
        if (isMaintenanceErrorBody(body)) {
          const params = body.errors.map(encodeURIComponent).join(',')
          window.location.href = `/maintenance?params=${params}`
          return
        }
        if (isHTTPError(body)) {
          dispatchAuthDialogByCode(body)
        }
      })
      .catch(() => {
        // 本文が JSON でない等で読めない場合は何もしない（各画面のエラー表示に委ねる）。
      })
  }
}

// TanStack Queryのデフォルト動作は便利だが、注意しないとデバッグが難しくなる可能性があるため、
// デフォルトでは安全側に立って余計なことをしない設定としている。
// これらの動作の必要性は使用する場面や実装者の経験によって異なるため、
// 各 Query or Mutation において設定を上書きして使用することを想定している。
// https://tanstack.com/query/latest/docs/react/guides/important-defaults
const safeQueryOptions = {
  networkMode: 'always',
  refetchInterval: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
  retry: false,
} as const

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) =>
      handleGlobalAuthError(error, query.meta as Record<string, unknown>),
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) =>
      handleGlobalAuthError(error, mutation.meta as Record<string, unknown>),
  }),
  defaultOptions: {
    queries: {
      ...safeQueryOptions,
      // その他のデフォルト設定はここに追記してください
    },
  },
})

type SetupTanStackQueryProps = {
  children: ReactNode
}

export const SetupTanStackQuery: FC<SetupTanStackQueryProps> = ({
  children,
}) => {
  return (
    <>
      <QueryClientProvider client={queryClient}>
        {children}
        {!!import.meta.env['VITE_APP_TAN_STACK_DEVTOOLS'] && (
          <ReactQueryDevtools />
        )}
      </QueryClientProvider>
    </>
  )
}
